import { Translator } from 'Translator';
import memoize from 'lodash.memoize';
import slugify from 'utils/slugify';

export type HostAggregations = {
  subcategory: string;
  host_count: number;
}[];

export interface PassionCategory {
  name: string;
  text: string;
  image: string;
  url: (
    experiencesAggregations?: any,
    hostAggregations?: HostAggregations,
  ) => string;
}

const i18n = (passion, defaultText = '') =>
  Translator.translateText(
    `SearchPage.PassionCategories.${passion}`,
    {},
    defaultText,
  );

const photoUrl = (key: string) =>
  `https://withlocals-com-res.cloudinary.com/image/upload/website/passions/slider/${key}`;

const targetUrl = (path) => (country, city) => {
  return () =>
    ['/s', slugify(country, false), slugify(city, false), path]
      .filter(Boolean)
      .join('/');
};

const PASSIONS = [
  {
    name: 'Foodies',
    text: i18n(
      'foodies',
      'Home Dinners • Local Delicacies • Food Markets • Beer & Wine',
    ),
    image: photoUrl('Foodies-optimized'),
    url: targetUrl(
      'tours/food-tours/?category[]=activities&subcategory[]=wine-beer-tours&subcategory[]=cooking-classes&subcategory[]=food-drink-tastings&subcategory[]=home-dinners',
    ),
  },
  {
    name: 'Families',
    text: i18n('families', 'Treasure Hunts • Arts & Crafts • Cooking Classes'),
    image: photoUrl('Family-optimized'),
    url: targetUrl('family-friendly/'),
  },
  {
    name: 'Night owls',
    text: i18n(
      'nightOwls',
      'Nightlife • Drinking Spots • Street Food • Sunsets',
    ),
    image: photoUrl('Night_owls-optimized'),
    url: targetUrl('tours/night-tours/'),
  },
  {
    name: 'History buffs',
    text: i18n(
      'historyBuffs',
      'Cultural Heritage • Castles • Myths & Legends • Museums',
    ),
    image: photoUrl('History_Buffs-optimized'),
    url: targetUrl('tours/city-tours/'),
  },
  {
    name: 'Newbies',
    text: i18n('newbies', 'Highlights & Must-sees • Tastings • Local Hotspots'),
    image: photoUrl('Newbies-optimized'),
    url: targetUrl('tours/city-tours/?subcategory[]=skip-the-line-tours'),
  },
  {
    name: 'Style icons',
    text: i18n(
      'styleIcons',
      'Personal Shopping • Thrift Markets • Vintage Clothes • Concept Stores',
    ),
    image: photoUrl('Style_icons-optimized'),
    url: targetUrl('tours/shopping-tours/'),
  },
  {
    name: 'Adventurers',
    text: i18n('adventurers', 'Boat Rides • Wildlife & Nature • Day Trips'),
    image: photoUrl('Adventure-optimized'),
    url: targetUrl(
      'tours/day-trips/?subcategory[]=multi-day-trips&subcategory[]=wildlife-nature',
    ),
  },
  {
    name: 'Art lovers',
    text: i18n(
      'artLovers',
      'Galleries • Street Art • Architecture • Art Workshops',
    ),
    image: photoUrl('Art_lovers-optimized'),
    url: targetUrl('tours/art-culture-tours/'),
  },
  {
    name: 'Explorers',
    text: i18n(
      'explorers',
      'Local Culture • Off-the-beaten-track • Markets • Hidden Gems',
    ),
    image: photoUrl('Explorers-optimized'),
    url: targetUrl('tours/off-the-beaten-track/'),
  },
  {
    name: 'Sporties',
    text: i18n('sporties', 'Running • Hiking • Bike Tours • Stadium Tours'),
    image: photoUrl('Sporties-optimized'),
    url: targetUrl('activities/sports-leisure/'),
  },
];

const getPassionsModel = (country: string, city: string): PassionCategory[] => {
  return PASSIONS.map(({ name, text, image, url }) => ({
    name,
    image,
    text,
    url: url(country, city),
  }));
};

export default memoize(
  getPassionsModel,
  (country, city) => `${country}.${city}`,
);
